import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns'
import { isEmpty } from 'lodash'

import { DATE_ONLY_FORMAT, INSIGHT_MAX_AGE_DAYS } from './AgenticInsights.constants'
import { getInsightScopeKeys, isCompletedExperiment, isLaunched } from './AgenticInsights.helpers'

// What a new run does to the page. The API returns an org's entire insight
// history and nothing is ever deleted — each run writes its own records — so
// these are display rules only:
//
//   Insights / Explore : the newest run for each filter set (in practice, each
//                        playbook), minus anything older than INSIGHT_MAX_AGE_DAYS.
//   Experiments        : every experiment that was ever started, from any run,
//                        plus the proposals from the newest run only.

// The calendar day a timestamp was recorded on, as `yyyy-MM-dd`. Taken straight
// off the string rather than through `new Date()`, which would move a late-evening
// UTC timestamp onto the next or previous day depending on the viewer.
const toDateOnly = (value) => (isEmpty(value) ? null : value.slice(0, 10))

export const formatDateOnly = (value, pattern = DATE_ONLY_FORMAT) => {
  const dateOnly = toDateOnly(value)
  return dateOnly ? format(parseISO(dateOnly), pattern) : null
}

const getFilterSetKey = (insight) => getInsightScopeKeys(insight)[0]

// Later createdAt wins; run ids break a tie, since they only ever increase.
// Both come from the same API serializer, so the ISO strings compare correctly.
const isNewerRun = (insight, current) => {
  if (insight.createdAt !== current.createdAt) {
    return insight.createdAt > current.createdAt
  }
  return insight.pipelineRunId > current.pipelineRunId
}

const getLatestRunKeyByFilterSet = (insights) => {
  const newestByFilterSet = {}
  insights.forEach((insight) => {
    const key = getFilterSetKey(insight)
    const current = newestByFilterSet[key]
    if (!current || isNewerRun(insight, current)) {
      newestByFilterSet[key] = insight
    }
  })
  return Object.fromEntries(
    Object.entries(newestByFilterSet).map(([key, insight]) => [key, insight.pipelineRunId])
  )
}

const isFromLatestRun = (insight, latestRunKeyByFilterSet) =>
  latestRunKeyByFilterSet[getFilterSetKey(insight)] === insight.pipelineRunId

const isWithinMaxAge = (insight, today) =>
  differenceInCalendarDays(today, parseISO(toDateOnly(insight.createdAt))) <= INSIGHT_MAX_AGE_DAYS

export const getCurrentRunInsights = (insights, today = new Date()) => {
  const latestRunKeyByFilterSet = getLatestRunKeyByFilterSet(insights)
  return insights.filter(
    (insight) => isFromLatestRun(insight, latestRunKeyByFilterSet) && isWithinMaxAge(insight, today)
  )
}

// Insights generate on the 1st, a team may not launch one until the 20th, and it
// then runs for 30 days — so a started experiment outlives the run that proposed
// it. Resetting it with the run would erase the record of what actually shipped.
export const getExperimentTabInsights = (insights, today = new Date()) => {
  const currentRunIds = getCurrentRunInsights(insights, today).map((insight) => insight.id)
  return insights.filter((insight) => isLaunched(insight) || currentRunIds.includes(insight.id))
}

// Experiments run for a fixed window, so the end date is start + days tracked:
// projected while running, actual once it finished. A cancelled experiment
// stopped early on a day the API doesn't record, so it gets no end date rather
// than a wrong one.
export const getExperimentDates = (insight) => {
  const { startDate, daysToTrack, status } = insight.experiment
  const startDay = toDateOnly(startDate)
  if (!startDay) {
    return { startDate: null, endDate: null }
  }
  const endDate =
    status === 'cancelled'
      ? null
      : format(addDays(parseISO(startDay), daysToTrack ?? 30), DATE_ONLY_FORMAT)
  return {
    startDate: formatDateOnly(startDay),
    endDate,
    endLabel: isCompletedExperiment(insight) ? 'Ended' : 'Ends',
  }
}
